import { lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import { RequireCapability, RequireRole } from './lib/auth.js';
import { AppShell } from './app/AppShell.js';
import { NotFound } from './app/NotFound.js';
import { PublicShell } from './app/PublicShell.js';

/**
 * Capability-aware routing. Public paths render inside the PublicShell; each
 * hat's surface renders inside the AppShell behind a capability guard, so one
 * login can hold several hats at once (SPEC.md "One person, multiple hats"). The
 * builder is gated on canBuild, not on the student role, so mentors and parents
 * reach it under the My Projects hat. Every feature dir is a lazily loaded,
 * wildcard-mounted subtree that owns its own internal routing (for example
 * /build for the project list and /build/:projectId for the editor).
 */
const Onboarding = lazy(() => import('./features/onboarding/index.js'));
const Settings = lazy(() => import('./features/onboarding/settings.js'));
const Builder = lazy(() => import('./features/builder/index.js'));
const Mentor = lazy(() => import('./features/mentor/index.js'));
const Parent = lazy(() => import('./features/parent/index.js'));
const Showcase = lazy(() => import('./features/showcase/index.js'));

export function AppRoutes() {
  return (
    <Routes>
      {/* Public: landing, auth, status screens, consent, and the showcase. */}
      <Route element={<PublicShell />}>
        <Route path="/" element={<Onboarding />} />
        <Route path="/login" element={<Onboarding />} />
        <Route path="/signup" element={<Onboarding />} />
        <Route path="/forgot-password" element={<Onboarding />} />
        <Route path="/pending" element={<Onboarding />} />
        <Route path="/paused" element={<Onboarding />} />
        {/* Finish the email password reset from the link's token (?token=...). */}
        <Route path="/reset-password" element={<Onboarding />} />
        {/* Parent consent from an invite email link; token is the builder id. */}
        <Route path="/consent/:token" element={<Onboarding />} />
        <Route path="/showcase/*" element={<Showcase />} />
      </Route>

      {/* Account settings for any signed-in role, inside the app shell. */}
      <Route
        element={
          <RequireRole>
            <AppShell />
          </RequireRole>
        }
      >
        <Route path="/settings" element={<Settings />} />
      </Route>

      {/* Build (My Projects hat): any account that can build, including a mentor
          or parent building for themselves. */}
      <Route
        element={
          <RequireCapability capability="build">
            <AppShell />
          </RequireCapability>
        }
      >
        <Route path="/build/*" element={<Builder />} />
      </Route>

      {/* Parent (My Kids hat): accounts with linked kids, plus a fresh parent-role
          account so it can reach its family home to add a first kid. */}
      <Route
        element={
          <RequireCapability capability="parent">
            <AppShell />
          </RequireCapability>
        }
      >
        <Route path="/parent/*" element={<Parent />} />
      </Route>

      {/* Mentor (My Students hat): mentor-role accounts. */}
      <Route
        element={
          <RequireCapability capability="mentor">
            <AppShell />
          </RequireCapability>
        }
      >
        <Route path="/mentor/*" element={<Mentor />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
